import { ExternalLink, Heading, Hint, Paragraph } from '@insify/components';
import { interpolate } from '@insify/ui-utils';
import { captureException } from '@sentry/react';
import parse, { Element, attributesToProps, domToReact } from 'html-react-parser';

import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser';

export function parseHTMLStringToReactComponent(
  htmlString: string,
  replacements?: Record<string, React.ComponentType>,
  interpolations?: Record<string, string>,
): string | JSX.Element | JSX.Element[] {
  const parserOptions: HTMLReactParserOptions = {
    replace(domNode) {
      if (domNode instanceof Element && domNode.attribs) {
        const props = attributesToProps(domNode.attribs);
        const children = domNode.children as DOMNode[];
        switch (domNode.name) {
          case 'externallink':
            return <ExternalLink href={props.href.toString()}>{domToReact(children, parserOptions)}</ExternalLink>;
          case 'hint':
            return <Hint buttonText={props.buttontext.toString()}>{domToReact(children, parserOptions)}</Hint>;
          case 'paragraph':
            return <Paragraph>{domToReact(children, parserOptions)}</Paragraph>;
          case 'heading':
            return <Heading {...props}>{domToReact(children, parserOptions)}</Heading>;
          default: {
            if (replacements) {
              const replacementsWithLowerCaseKeys = Object.entries(replacements).reduce<
                Record<string, React.ComponentType>
              >((acc, [key, value]) => {
                acc[key.toLowerCase()] = value;
                return acc;
              }, {});
              if (domNode.name in replacementsWithLowerCaseKeys) {
                const Component = replacementsWithLowerCaseKeys[domNode.name];
                return <Component {...props}>{domToReact(children, parserOptions)}</Component>;
              }
            }
          }
        }
      }
      return;
    },
  };

  if (interpolations) htmlString = interpolate(htmlString, interpolations);

  return parse(htmlString, parserOptions);
}

type UseTranslationsFileProps<T> = {
  sendErrorToSentry: boolean;
  translations: T;
  replacements?: Record<string, React.ComponentType>;
  interpolations?: Record<string, string>;
};

export const useTranslationsFile = <T,>(props: UseTranslationsFileProps<T>): T => {
  const handleError = (error: Error) => {
    if (props.sendErrorToSentry) {
      captureException(error);
    } else {
      throw error;
    }
  };

  // Generate a proxy to handle getting values from the translations object, it should work with nested objects.
  const handler = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(target: any, prop: string): boolean | string | JSX.Element | JSX.Element[] | undefined {
      if (typeof prop === 'symbol') {
        return undefined;
      }

      if (!(prop in target)) {
        handleError(new Error(`Translation key "${String(prop)}" not found in translations object`));
        return undefined;
      }
      const value = target[prop];

      if (Array.isArray(value)) {
        return value;
      }

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'object' && value !== null) {
        return new Proxy(value, handler);
      }

      if (typeof value === 'string') {
        return parseHTMLStringToReactComponent(value, props.replacements, props.interpolations);
      }

      handleError(new Error(`Translation key "${String(prop)}" not found in translations object`));

      return;
    },
  };

  return new Proxy(props.translations, handler) as T;
};
